import { ActionIcon, Button, Group, Tooltip } from '@mantine/core';
import { IconCalendarEvent, IconVideo, IconX } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { CopyMeetingLinkButton } from '@/components/CopyMeetingLinkButton';
import { AppointmentTitleText } from '@/pages/appointments/utils/AppointmentTitleText';
import {
  appointmentEditorNav,
  type AppointmentEditorSource,
} from './appointmentEditorNav';

export interface AppointmentEditorHeaderProps {
  source: AppointmentEditorSource;
  title: string;
  appointmentId?: string;
  clientDisplayName?: string;
  clientChartHref?: string;
  videoLink?: string;
  showJoinVideo?: boolean;
  showCopyMeetingLink?: boolean;
  onClose: () => void;
  onJoinVideo?: () => void;
}

export function AppointmentEditorHeader({
  source,
  title,
  appointmentId,
  clientDisplayName,
  clientChartHref,
  videoLink,
  showJoinVideo = false,
  showCopyMeetingLink = false,
  onClose,
  onJoinVideo,
}: AppointmentEditorHeaderProps): JSX.Element {
  const navigate = useNavigate();
  const nav = appointmentId ? appointmentEditorNav(source, appointmentId) : null;

  return (
    <Group justify="space-between" align="center" style={{ width: '100%' }}>
      <Group gap="sm" wrap="nowrap">
        <AppointmentTitleText
          title={title}
          clientDisplayName={clientDisplayName}
          clientChartHref={clientChartHref}
          fw={700}
          size="lg"
        />
      </Group>
      <Group gap="xs">
        {nav ? (
          <Tooltip label={nav.label}>
            <ActionIcon
              size="lg"
              variant="light"
              color="blue"
              aria-label={nav.label}
              onClick={() => {
                navigate(nav.href);
                onClose();
              }}
            >
              <IconCalendarEvent size={20} />
            </ActionIcon>
          </Tooltip>
        ) : null}
        <CopyMeetingLinkButton link={showCopyMeetingLink ? videoLink : undefined} />
        {showJoinVideo && appointmentId ? (
          <Button
            size="sm"
            variant="filled"
            color="gold"
            leftSection={<IconVideo size={16} />}
            onClick={() => {
              if (onJoinVideo) {
                onJoinVideo();
                return;
              }
              navigate(`/dashboard/session/${appointmentId}`);
            }}
          >
            Join
          </Button>
        ) : null}
        <ActionIcon size="lg" variant="subtle" color="gray" onClick={onClose}>
          <IconX size={20} />
        </ActionIcon>
      </Group>
    </Group>
  );
}
